import React, { useState } from 'react';
import { Layout, Form, Input, Typography, Button, Tooltip, Flex, theme, message, Card } from 'antd';
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  ArrowLeftOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { HttpRequest } from '../../../../../../utils/httpClient';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');


const { Content } = Layout;
const { Title, Text } = Typography;
const { useToken } = theme;

interface FormAuthIntegrationProps {
  onBack: () => void;
  integration: {
    name: string;
    logo: string;
    id: string;
    authFields: Array<{
      name: string;
      label: string;
      placeholder: string;
      type: string;
      required: boolean;
      order: number;
    }>;
  };
  chatbotId: string;
  onAuthSuccess: () => void;
}

export const FormAuthIntegration: React.FC<FormAuthIntegrationProps> = ({
  onBack,
  integration,
  chatbotId,
  onAuthSuccess,
}) => {
  const { token } = useToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Estado para mostrar/ocultar la card de instrucciones
  const [showTokenCard, setShowTokenCard] = useState(false);
  const toggleTokenCard = () => setShowTokenCard((prev) => !prev);

  const handleConnect = async (values: any) => {
    try {
      setLoading(true);

      const request = new HttpRequest(`${process.env.REACT_APP_API_HOST}/integration/auth`);
      const payload = {
        integrationId: integration.id,
        chatbotId: chatbotId,
        authData: values,
        isAuthenticated: true,
      };

      await request.post(payload);

      message.success(`Successfully connected to ${integration.name}`);
      onAuthSuccess();
    } catch (error) {
      logger.error('Error authenticating:', error);
      message.error('Failed to authenticate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ background: token.colorBgContainer }}>
      <Content>
        <Flex vertical align="center" style={{ padding: token.paddingLG }}>
          <Flex align="center" gap={token.marginSM} style={{ width: '100%', maxWidth: 650 }}>
            <Flex vertical align="center" style={{ flex: 1 }}>
              <img
                src={integration.logo}
                alt={`${integration.name} Logo`}
                style={{
                  height: 80,
                  width: 80,
                  objectFit: 'contain',
                  marginBottom: token.marginXS,
                }}
              />
              <Title level={4} style={{ margin: 0 }}>
                {integration.name} Connection
              </Title>
            </Flex>
          </Flex>

          <Form
            form={form}
            layout="vertical"
            style={{ width: '100%', maxWidth: 650 }}
            onFinish={handleConnect}
          >
            {integration.authFields
              .sort((a, b) => a.order - b.order)
              .map((field) => (
                <Form.Item
                  key={field.name}
                  labelCol={{ style: { width: '100%' } }}
                  label={
                    <div style={{ display: 'flex', width: '100%' }}>
                      <span>{field.label}</span>
                      {field.name === 'API Token' && (
                        <div
                          style={{ flex: 1, display: 'flex', justifyContent: 'end', width: '100%' }}
                        >
                          <Text
                            style={{
                              cursor: 'pointer',
                              color: token.colorPrimary,
                            }}
                            onClick={toggleTokenCard}
                          >
                            How to get API token?
                          </Text>
                        </div>
                      )}
                    </div>
                  }
                  name={field.name}
                  rules={[
                    {
                      required: field.required,
                      message: `Please input your ${field.label.toLowerCase()}!`,
                    },
                  ]}
                >
                  {field.type === 'password' ? (
                    <Input.Password
                      placeholder={field.placeholder}
                      iconRender={(visible) =>
                        visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                      }
                    />
                  ) : (
                    <Input type={field.type} placeholder={field.placeholder} />
                  )}

                  {/* Card de instrucciones, aparece sólo si showTokenCard es true */}
                  {field.name === 'API Token' && showTokenCard && (
                    <Card
                      title={
                        <Flex align="center" gap={token.padding}>
                          <InfoCircleOutlined
                            style={{ color: token.colorPrimary, fontSize: token.fontSizeLG }}
                          />
                          <Text strong>How to get your API token</Text>
                        </Flex>
                      }
                      style={{
                        width: '100%',
                        marginTop: token.marginLG,
                        backgroundColor: token.colorPrimaryBg,
                        padding: 0,
                      }}
                    >
                      <Flex vertical gap={token.paddingXS} style={{ paddingLeft: token.paddingXL }}>
                        <Text>
                          1. Go to <strong>Atlassian Account Settings</strong>
                        </Text>
                        <Text>
                          2. Select <strong>Security from the sidebar</strong>
                        </Text>
                        <Text>
                          3. Click <strong>Create and manage API tokens</strong>
                        </Text>
                        <Text>
                          4. Click <strong>Create API token</strong>
                        </Text>
                        <Text>
                          5. Name your token and click <strong>Create</strong>
                        </Text>
                        <Text>6. Copy your token and paste it here</Text>
                      </Flex>
                    </Card>
                  )}
                </Form.Item>
              ))}

            <Text type="secondary" style={{ display: 'block', marginBottom: token.marginLG }}>
              Review this app's{' '}
              <a
                href="https://docs.toolsplus.io/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                privacy policy
              </a>{' '}
              and{' '}
              <a
                href="https://docs.toolsplus.io/legal/end-user-license-agreement"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                terms of service
              </a>
              . Revoke access by viewing your authorized apps in{' '}
              <Text type="secondary" underline>
                your account
              </Text>
              .
            </Text>

            <Flex gap={token.marginSM}>
              <Button style={{ flex: 1 }} onClick={onBack}>
                Cancel
              </Button>
              <Button type="primary" style={{ flex: 1 }} htmlType="submit" loading={loading}>
                Test
              </Button>
            </Flex>
          </Form>
        </Flex>
      </Content>
    </Layout>
  );
};
